import React, { useCallback, useEffect, useRef, useState } from 'react';

import { LeftIcon, RightIcon } from '@travel/ui/components/NavigationButton/navigationIcon';
import cx from '@travel/ui/utils/classnames';

import { useOnResizeDebounce } from '@travel/traveler-core/hooks';

import styles from './horizontalNavScroll.module.scss';

type HorizontalNavScrollProps = {
  children: React.ReactNode;
};

const HorizontalNavScroll: React.FC<HorizontalNavScrollProps> = ({ children }) => {
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleScroll = useCallback(() => {
    const nav = scrollContentRef.current;
    if (nav) {
      setShowLeftArrow(nav.scrollLeft > 0);
      setShowRightArrow(nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 1);
    }
  }, []);

  const onClickLeftButton = () => {
    const nav: HTMLElement | null = scrollContentRef.current;
    if (nav) {
      const scrollAmount = nav.clientWidth - 119; // container width - button size - edge
      nav.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const onClickRightButton = () => {
    const nav: HTMLElement | null = scrollContentRef.current;
    if (nav) {
      const scrollAmount = nav.clientWidth - 120; // container width - button size
      nav.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const onMouseDown = (e: React.MouseEvent) => {
    const nav = scrollContentRef.current;
    if (nav) {
      isDragging.current = true;
      startX.current = e.pageX - nav.offsetLeft;
      scrollLeft.current = nav.scrollLeft;
    }
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const nav = scrollContentRef.current;
    if (nav) {
      const x = e.pageX - nav.offsetLeft;
      const walk = x - startX.current;
      nav.scrollLeft = scrollLeft.current - walk;
    }
  };

  const onMouseUp = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    handleScroll();
  }, [children, handleScroll]);

  useEffect(() => {
    const nav = scrollContentRef.current;
    if (nav) {
      nav.addEventListener('scroll', handleScroll);
      return () => {
        nav.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  useOnResizeDebounce(handleScroll);

  return (
    <div className={styles.scrollSection}>
      <button
        data-testid="left-nav-button"
        className={cx(styles.arrowButton, styles.arrowLeftButton, showLeftArrow && styles.show)}
        tabIndex={-1}
        onClick={onClickLeftButton}
        onTouchStart={onClickLeftButton}
        type="button"
      >
        <LeftIcon />
      </button>

      <div
        ref={scrollContentRef}
        data-testid="scrollable-content"
        className={styles.contentNav}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {children}
      </div>

      <button
        data-testid="right-nav-button"
        className={cx(styles.arrowButton, styles.arrowRightButton, showRightArrow && styles.show)}
        tabIndex={-1}
        onClick={onClickRightButton}
        onTouchStart={onClickRightButton}
        type="button"
      >
        <RightIcon />
      </button>
    </div>
  );
};

export default HorizontalNavScroll;
